'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { siteName } from '@/lib/site';
import { strings } from '@/content/strings';
import { useAuthSession } from '@/app/components/auth/useAuthSession';

type PublicMenuButtonProps = {
  variant?: 'light' | 'dark';
  contextName?: string;
};

export default function PublicMenuButton({ variant = 'light', contextName }: PublicMenuButtonProps) {
  const { data: session, status } = useAuthSession();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const currentPath = useMemo(() => {
    const query = searchParams.toString();
    return query ? `${pathname}?${query}` : pathname;
  }, [pathname, searchParams]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('click', handleClick);
    window.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('click', handleClick);
      window.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const buttonBase =
    variant === 'dark'
      ? 'border border-border/60 text-card-foreground bg-card/30 hover:bg-card/40'
      : 'border border-border text-foreground bg-card hover:bg-ghost';
  const menuBase =
    variant === 'dark'
      ? 'border border-border/60 bg-card/95 text-card-foreground'
      : 'border border-border bg-card text-foreground';

  const displayName = session?.user?.name?.trim() || session?.user?.email?.trim() || siteName;
  const firstName = displayName.split(/\s+/).filter(Boolean)[0] || siteName;
  const email = session?.user?.email?.trim() || '';
  const shouldShowEmail = !!email && session?.user;

  const truncateMiddle = (value: string, maxLength = 24) => {
    if (value.length <= maxLength) return value;
    const keepStart = Math.max(2, Math.floor((maxLength - 3) / 2));
    const keepEnd = Math.max(2, maxLength - 3 - keepStart);
    return `${value.slice(0, keepStart)}...${value.slice(-keepEnd)}`;
  };
  const loginContext = contextName?.trim() || siteName;

  const handleSignOut = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ callbackURL: '/' }),
        credentials: 'include',
      });
    } catch {
      // ignore and continue
    }
    window.location.assign('/');
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`inline-flex h-10 w-10 items-center justify-center rounded-full text-xs font-semibold ${buttonBase}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={strings.common.buttons.menu || 'Menu'}
      >
        <span className="flex h-4 w-5 flex-col justify-between">
          <span className="h-[2px] w-full rounded-full bg-current" />
          <span className="h-[2px] w-full rounded-full bg-current" />
          <span className="h-[2px] w-full rounded-full bg-current" />
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className={`absolute right-0 mt-2 w-44 rounded-xl shadow-lg ${menuBase}`}
        >
          <div className="flex flex-col p-2 text-sm">
            {session?.user && (
              <Link
                href="/bookings"
                className="px-3 py-2 text-xs font-semibold text-muted-foreground hover:text-foreground"
                role="menuitem"
                onClick={() => setOpen(false)}
              >
                Hi, {truncateMiddle(firstName, 20)}
              </Link>
            )}
            <Link
              href="/bookings"
              className="rounded-lg px-3 py-2 font-semibold hover:bg-ghost"
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              {strings.common.nav.myBooking}
            </Link>
            {session?.user ? (
              <button
                type="button"
                onClick={handleSignOut}
                className="rounded-lg px-3 py-2 text-left font-semibold hover:bg-ghost"
                role="menuitem"
              >
                {strings.common.buttons.logout}
              </button>
            ) : (
              <Link
                href={`/login?callbackUrl=${encodeURIComponent(currentPath)}&contextName=${encodeURIComponent(
                  loginContext
                )}`}
                className="rounded-lg px-3 py-2 font-semibold hover:bg-ghost"
                role="menuitem"
                onClick={() => setOpen(false)}
              >
                {strings.common.buttons.login}
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
